import { useRef } from "react";
import { PRESET_LOGOS, DOT_TYPES, CORNER_SQUARE_TYPES, CORNER_DOT_TYPES, COLOR_PRESETS } from "@/lib/presetLogos";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_LOGO_SIZE = 100 * 1024; // 100KB

export default function QRStylePanel({ style, onChange }) {
  const fileInputRef = useRef(null);

  const update = (key, value) => {
    onChange({ ...style, [key]: value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE) {
      toast.error("Logo must be under 100KB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange({ ...style, logo_base64: reader.result, logo_preset: null });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const selectPresetLogo = (logoId) => {
    if (style.logo_preset === logoId) {
      onChange({ ...style, logo_preset: null, logo_base64: null });
    } else {
      onChange({ ...style, logo_preset: logoId, logo_base64: null });
    }
  };

  const removeLogo = () => {
    onChange({ ...style, logo_base64: null, logo_preset: null });
  };

  const sectionTitle = "text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3";

  return (
    <div className="space-y-6" data-testid="qr-style-panel">
      {/* Dot Patterns */}
      <div>
        <p className={sectionTitle}>Pattern</p>
        <div className="grid grid-cols-3 gap-2">
          {DOT_TYPES.map((dt) => (
            <button
              key={dt.id}
              data-testid={`dot-type-${dt.id}`}
              onClick={() => update("dot_type", dt.id)}
              className={`rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all ${
                style.dot_type === dt.id
                  ? "border-[#0047FF] bg-[#0047FF]/5 text-[#0047FF]"
                  : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >
              {dt.name}
            </button>
          ))}
        </div>
      </div>

      {/* Corner Styles */}
      <div>
        <p className={sectionTitle}>Corners</p>
        <div className="grid grid-cols-3 gap-2">
          {CORNER_SQUARE_TYPES.map((ct) => (
            <button
              key={ct.id}
              data-testid={`corner-type-${ct.id}`}
              onClick={() => update("corner_square_type", ct.id)}
              className={`rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all ${
                style.corner_square_type === ct.id
                  ? "border-[#0047FF] bg-[#0047FF]/5 text-[#0047FF]"
                  : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >
              {ct.name}
            </button>
          ))}
        </div>
      </div>

      {/* Corner Dots */}
      <div>
        <p className={sectionTitle}>Corner Dots</p>
        <div className="grid grid-cols-2 gap-2">
          {CORNER_DOT_TYPES.map((cd) => (
            <button
              key={cd.id}
              data-testid={`corner-dot-${cd.id}`}
              onClick={() => update("corner_dot_type", cd.id)}
              className={`rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all ${
                style.corner_dot_type === cd.id
                  ? "border-[#0047FF] bg-[#0047FF]/5 text-[#0047FF]"
                  : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >
              {cd.name}
            </button>
          ))}
        </div>
      </div>

      {/* Code Color */}
      <div>
        <p className={sectionTitle}>Code Color</p>
        <div className="flex items-center gap-2 flex-wrap">
          {COLOR_PRESETS.filter(c => c !== "#FFFFFF").map((color) => (
            <button
              key={`fg-${color}`}
              data-testid={`fg-color-${color.replace("#", "")}`}
              onClick={() => update("fg_color", color)}
              className={`h-7 w-7 rounded-full border-2 transition-all ${
                style.fg_color === color ? "border-[#0047FF] scale-110 ring-2 ring-[#0047FF]/30" : "border-zinc-200 hover:scale-105"
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
          <div className="relative">
            <input
              type="color"
              value={style.fg_color || "#09090B"}
              onChange={(e) => update("fg_color", e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer w-7 h-7"
              data-testid="fg-color-custom"
            />
            <div className="h-7 w-7 rounded-full border-2 border-dashed border-zinc-300 flex items-center justify-center cursor-pointer hover:border-zinc-400">
              <span className="text-[8px] font-bold text-zinc-400">+</span>
            </div>
          </div>
        </div>
      </div>

      {/* Background Color */}
      <div>
        <p className={sectionTitle}>Background</p>
        <div className="flex items-center gap-2 flex-wrap">
          {["#FFFFFF", "#F4F4F5", "#FAFAFA", "#FEF3C7", "#ECFDF5", "#EFF6FF", "#FDF2F8", "#09090B"].map((color) => (
            <button
              key={`bg-${color}`}
              data-testid={`bg-color-${color.replace("#", "")}`}
              onClick={() => update("bg_color", color)}
              className={`h-7 w-7 rounded-full border-2 transition-all ${
                style.bg_color === color ? "border-[#0047FF] scale-110 ring-2 ring-[#0047FF]/30" : "border-zinc-200 hover:scale-105"
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
          <div className="relative">
            <input
              type="color"
              value={style.bg_color || "#FFFFFF"}
              onChange={(e) => update("bg_color", e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer w-7 h-7"
              data-testid="bg-color-custom"
            />
            <div className="h-7 w-7 rounded-full border-2 border-dashed border-zinc-300 flex items-center justify-center cursor-pointer hover:border-zinc-400">
              <span className="text-[8px] font-bold text-zinc-400">+</span>
            </div>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div>
        <p className={sectionTitle}>Center Logo</p>

        {/* Current logo indicator */}
        {(style.logo_base64 || style.logo_preset) && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-zinc-50 rounded-lg border border-zinc-200">
            {style.logo_base64 && (
              <img src={style.logo_base64} alt="Custom logo" className="h-8 w-8 rounded object-contain" />
            )}
            {style.logo_preset && (
              <img
                src={PRESET_LOGOS.find((p) => p.id === style.logo_preset)?.url}
                alt={style.logo_preset}
                className="h-8 w-8 rounded"
              />
            )}
            <span className="text-xs font-medium text-zinc-600 flex-1">
              {style.logo_base64 ? "Custom logo" : PRESET_LOGOS.find((p) => p.id === style.logo_preset)?.name}
            </span>
            <button
              data-testid="remove-logo"
              onClick={removeLogo}
              className="rounded p-1 hover:bg-zinc-200 transition-colors"
            >
              <X className="h-3.5 w-3.5 text-zinc-500" />
            </button>
          </div>
        )}

        {/* Upload */}
        <button
          data-testid="upload-logo-button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-200 px-4 py-3 text-xs font-bold text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 transition-all mb-3"
        >
          <Upload className="h-3.5 w-3.5" />
          Upload Logo (max 100KB)
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          className="hidden"
          data-testid="logo-file-input"
        />

        {/* Preset social logos */}
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Or choose a brand</p>
        <div className="grid grid-cols-4 gap-2">
          {PRESET_LOGOS.map((logo) => (
            <button
              key={logo.id}
              data-testid={`preset-logo-${logo.id}`}
              onClick={() => selectPresetLogo(logo.id)}
              className={`flex flex-col items-center gap-1 rounded-lg border-2 p-2 transition-all ${
                style.logo_preset === logo.id
                  ? "border-[#0047FF] bg-[#0047FF]/5"
                  : "border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <img src={logo.url} alt={logo.name} className="h-6 w-6 rounded" />
              <span className="text-[9px] font-bold text-zinc-500 truncate w-full text-center">{logo.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className={sectionTitle}>Bottom Text</p>
        <input
          data-testid="bottom-text-input"
          type="text"
          value={style.bottom_text || ""}
          onChange={(e) => update("bottom_text", e.target.value)}
          placeholder="e.g. Scan to view menu"
          className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF] transition-all bg-white outline-none"
        />
      </div>

      <div>
        <p className={sectionTitle}>Review Stars</p>
        <div className="flex items-center justify-between gap-3">
          <button
            data-testid="toggle-review-stars"
            onClick={() => update("show_review_stars", !style.show_review_stars)}
            className={`rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all ${
              style.show_review_stars
                ? "border-[#0047FF] bg-[#0047FF]/5 text-[#0047FF]"
                : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
            }`}
          >
            {style.show_review_stars ? "Enabled" : "Disabled"}
          </button>

          <div className="flex items-center gap-2 flex-1">
            <input
              data-testid="review-rating-range"
              type="range"
              min={1}
              max={5}
              step={1}
              value={style.review_rating || 5}
              onChange={(e) => update("review_rating", Number(e.target.value))}
              disabled={!style.show_review_stars}
              className="w-full"
            />
            <span className={`text-xs font-bold tabular-nums ${style.show_review_stars ? "text-zinc-700" : "text-zinc-400"}`}>
              {style.review_rating || 5}/5
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
